/**
 * demoEnricher.js — the SYNTHESIS half of demo mode.
 *
 * The redactor ({@link module:app/demo/demoRedactor}) anonymises data that a
 * tenant already has. But a fresh / small tenant has empty screens — no orders,
 * no leads, dead dashboards. This module fills that gap: for GET responses on a
 * curated ALLOW-LIST of read-only list/stats endpoints, it tops an empty or
 * sparse list up to a believable count with fabricated rows
 * ({@link module:app/demo/demoBlueprints}) and synthesises server-side stat
 * objects — so every screen looks like a busy, live system.
 *
 * Hard safety rules (why an allow-list, not a deny-list):
 *   • NEVER fabricate into operational / write / money-critical flows. The live
 *     POS order board, `/order/create`, payments, the food MENU, warehouse
 *     inbound/reservation/packing, day-open/close, mail (a real mailbox) and
 *     support history are all DELIBERATELY absent from the registry, so they pass
 *     through untouched (redaction only).
 *   • Only GET. Only page 1 (so infinite-scroll can't keep generating forever).
 *   • Only top-up: a screen that already has enough real rows is left alone.
 *   • Fail-open: any error returns the input unchanged.
 *
 * @module app/demo/demoEnricher
 */

import {isDemoMode} from './demoState';
import {buildEntities, hasBlueprint} from './demoBlueprints';

/** Default fabricated-row target; orders get more so charts are dense. */
const DEFAULT_TARGET = 24;
const TARGET_BY_RESOURCE = {order: 44};

/**
 * The allow-list. Ordered; first match wins. `re` tests the request PATH with any
 * leading `/api` stripped. `list` endpoints top up an array; `stats` endpoints
 * synthesise an object.
 * @type {Array<{re: RegExp, resource: string, kind: 'list'|'stats', keys?: string[]}>}
 */
const REGISTRY = [
    // Orders — the dated statistics archive + the management order list ONLY.
    // The LIVE POS board (`/restaurant/data/{id}/orders`) is intentionally absent.
    {re: /^\/restaurant\/data\/orders$/, resource: 'order', kind: 'list', keys: ['orders']},
    {re: /^\/restaurant\/[^/]+\/order$/, resource: 'order', kind: 'list', keys: ['orders']},

    // Service module
    {re: /^\/serviceModule\/work-orders$/, resource: 'workOrder', kind: 'list', keys: ['workOrders', 'worksheets', 'docs']},
    {re: /^\/service\/worksheets$/, resource: 'workOrder', kind: 'list', keys: ['workOrders', 'worksheets', 'docs']},
    {re: /^\/serviceModule\/customers$/, resource: 'customer', kind: 'list', keys: ['customers', 'docs']},
    {re: /^\/service\/customers$/, resource: 'customer', kind: 'list', keys: ['customers', 'docs']},
    {re: /^\/serviceModule\/technicians$/, resource: 'technician', kind: 'list', keys: ['technicians']},
    {re: /^\/serviceModule\/appointments$/, resource: 'appointment', kind: 'list', keys: ['appointments']},

    // CRM
    {re: /^\/crm\/leads$/, resource: 'lead', kind: 'list', keys: ['leads', 'docs']},
    {re: /^\/crm\/statistics$/, resource: 'crmStats', kind: 'stats'},
    {re: /^\/quotes$/, resource: 'quote', kind: 'list', keys: ['quotes', 'docs']},

    // Finance / invoicing
    {re: /nav-invoices$/, resource: 'navInvoice', kind: 'list', keys: ['invoices', 'navInvoices']},
    {re: /^\/navInvoices$/, resource: 'navInvoice', kind: 'list', keys: ['invoices', 'navInvoices']},
    {re: /^\/banks$/, resource: 'bank', kind: 'list', keys: ['banks']},
    {re: /^\/finance\/registers$/, resource: 'register', kind: 'list', keys: ['registers']},
    {re: /^\/costTypes$/, resource: 'costType', kind: 'list', keys: ['costTypes']},

    // Warehouse READ lists (operational inbound/reservation/packing are excluded)
    {re: /^\/warehouse\/all\/stock$/, resource: 'stockItem', kind: 'list', keys: ['stock']},
    {re: /^\/warehouse\/getRestaurantTransactions$/, resource: 'transaction', kind: 'list', keys: ['transactions']},
    {re: /^\/warehouse\/warehouses$/, resource: 'warehouse', kind: 'list', keys: ['warehouses']},

    // People / config / CRM extras
    {re: /^\/restaurant\/admin$/, resource: 'user', kind: 'list', keys: ['admins', 'users', 'staff']},
    {re: /^\/restaurant\/guests$/, resource: 'guest', kind: 'list', keys: ['guests']},
    {re: /^\/vipcard\/list$/, resource: 'vipCard', kind: 'list', keys: ['vipCards', 'cards', 'docs']},
    {re: /^\/integrations$/, resource: 'integration', kind: 'list', keys: ['integrations']},
    {re: /^\/automations$/, resource: 'automation', kind: 'list', keys: ['docs', 'items']},

    // Bookings / calendar (GET lists only; create/update/delete are not GET)
    {re: /^\/booking\/restaurant$/, resource: 'booking', kind: 'list', keys: ['bookings']},
    {re: /^\/calendar\/events$/, resource: 'calendarEvent', kind: 'list', keys: ['docs', 'data', 'events']},
    {re: /^\/calendar\/shared$/, resource: 'calendarEvent', kind: 'list', keys: ['docs', 'data', 'events']},
    {re: /^\/calendar\/feed$/, resource: 'calendarEvent', kind: 'list', keys: ['docs', 'data', 'events']},
    {re: /^\/calendar\/experts$/, resource: 'calendarExpert', kind: 'list', keys: ['experts', 'data', 'docs']},

    // HACCP (temperature / cleaning logs)
    {re: /^\/haccp\/equipment$/, resource: 'haccpEquipment', kind: 'list', keys: ['equipment', 'data', 'docs']},
    {re: /^\/haccp\/records$/, resource: 'haccpRecord', kind: 'list', keys: ['records', 'data', 'docs']},

    // VoIP call log + directory (recent-calls/directory/contacts read the same shapes)
    {re: /^\/voip\/calls$/, resource: 'voipCall', kind: 'list', keys: ['calls', 'data', 'docs']},
    {re: /^\/voip\/recent-calls$/, resource: 'voipCall', kind: 'list', keys: ['recentCalls', 'calls', 'data']},
    {re: /^\/voip\/directory$/, resource: 'voipContact', kind: 'list', keys: ['directory', 'contacts', 'data']},
    {re: /^\/voip\/contacts$/, resource: 'voipContact', kind: 'list', keys: ['contacts', 'data', 'docs']},
];

/** Keys under which a total/count is stored, kept in sync after a top-up. */
const TOTAL_KEYS = ['total', 'totalCount', 'count', 'totalDocs', 'totalItems', 'totalResults'];

/**
 * Split a URL into a normalised path (no query, no leading `/api`) + a query map.
 * @param {string} url the request URL.
 * @returns {{path: string, query: Record<string,string>}} parsed parts.
 */
function parseUrl(url) {
    const raw = String(url || '');
    const noHost = raw.replace(/^https?:\/\/[^/]+/i, '');
    const qIdx = noHost.indexOf('?');
    const pathRaw = qIdx >= 0 ? noHost.slice(0, qIdx) : noHost;
    const queryRaw = qIdx >= 0 ? noHost.slice(qIdx + 1) : '';
    const path = pathRaw.replace(/\/$/, '').replace(/^\/api(?=\/)/, '') || '/';

    const query = {};
    queryRaw.split('&').forEach((pair) => {
        if (!pair) {
            return;
        }
        const eq = pair.indexOf('=');
        const k = decodeURIComponent(eq >= 0 ? pair.slice(0, eq) : pair);
        const v = eq >= 0 ? decodeURIComponent(pair.slice(eq + 1)) : '';
        query[k] = v;
    });

    return {path, query};
}

/**
 * Locate the list array inside a response envelope.
 * @param {*} json the (already redacted) response.
 * @param {string[]} keys candidate envelope keys for this resource.
 * @returns {null|{bare: true, arr: any[]}|{container: object, key: string, arr: any[]}}
 */
function locateList(json, keys) {
    if (Array.isArray(json)) {
        return {bare: true, arr: json};
    }
    if (!json || typeof json !== 'object') {
        return null;
    }
    const tryKeys = [...(keys || []), 'data', 'docs', 'items', 'result', 'rows'];
    for (const k of tryKeys) {
        if (Array.isArray(json[k])) {
            return {container: json, key: k, arr: json[k]};
        }
    }
    for (const parent of ['data', 'result', 'body']) {
        const p = json[parent];
        if (p && typeof p === 'object' && !Array.isArray(p)) {
            for (const k of tryKeys) {
                if (Array.isArray(p[k])) {
                    return {container: p, key: k, arr: p[k]};
                }
            }
        }
    }

    return null;
}

/**
 * Build the fabrication context from the request (date window, scope, direction).
 * @param {string} path normalised path.
 * @param {Record<string,string>} query query map.
 * @param {string} resource resource key.
 * @returns {object} the blueprint context.
 */
function buildContext(path, query, resource) {
    // Quantise "now" to the hour so repeated fetches within a session fabricate
    // byte-identical rows (no flicker / reshuffle on poll), while the data still
    // freshens hour-to-hour so it keeps looking live over a long demo.
    const nowMs = Math.floor(Date.now() / 3600000) * 3600000;
    const toMs = (v) => {
        const t = v ? new Date(v).getTime() : NaN;

        return Number.isNaN(t) ? null : t;
    };
    const startMs = toMs(query.startDate) || toMs(query.dateFrom) || (nowMs - 14 * 86400000);
    const endMs = toMs(query.endDate) || toMs(query.dateTo) || nowMs;
    const pathId = (/^\/restaurant\/([^/]+)\/order$/.exec(path) || [])[1];
    const restaurantId = (pathId && pathId !== 'data') ? pathId : (query.restaurantId || '');
    const direction = (query.direction || '').toUpperCase();

    return {
        nowMs,
        startMs: Math.min(startMs, endMs),
        endMs: Math.max(startMs, endMs),
        restaurantId,
        placeId: query.placeId || '',
        direction: direction === 'INBOUND' || direction === 'OUTBOUND' ? direction : '',
        seedSalt: `${resource}|${query.dateFrom || query.startDate || ''}|${restaurantId}|${direction}`,
    };
}

/**
 * Read a CRM stat with the same fallback chain the screens use.
 * @param {object} o stats object.
 * @param {string[]} names alias keys.
 * @returns {number} the first numeric value, or 0.
 */
function statOf(o, names) {
    for (const n of names) {
        const v = Number(o && o[n]);
        if (Number.isFinite(v) && v !== 0) {
            return v;
        }
    }

    return 0;
}

/**
 * Synthesise a believable CRM statistics object, preserving any real non-zero
 * values that are already present.
 * @param {object} existing the redacted stats object (`data.statistics || data`).
 * @returns {object} a filled stats object.
 */
function synthCrmStats(existing) {
    const cur = existing && typeof existing === 'object' ? existing : {};
    const total = statOf(cur, ['totalLeads', 'leadsCount', 'total']) || 128 + (Date.now() % 40);
    const won = statOf(cur, ['wonLeads', 'converted', 'conversions']) || Math.round(total * 0.34);
    const lost = statOf(cur, ['lostLeads', 'lost']) || Math.round(total * 0.12);
    const inProgress = statOf(cur, ['inProgress', 'contacted']) || Math.round(total * 0.28);
    const newLeads = statOf(cur, ['newLeads', 'new']) || Math.max(0, total - won - lost - inProgress);
    const pipelineValue = statOf(cur, ['pipelineValue', 'totalValue', 'value']) || total * 185000;

    return {
        ...cur,
        totalLeads: total,
        leadsCount: total,
        total,
        wonLeads: won,
        converted: won,
        conversions: won,
        lostLeads: lost,
        lost,
        inProgress,
        contacted: inProgress,
        newLeads,
        new: newLeads,
        pipelineValue,
        totalValue: pipelineValue,
        value: pipelineValue,
    };
}

/**
 * Whether a stats object is "empty" (worth synthesising).
 * @param {object} o stats object.
 * @returns {boolean} true when it carries no meaningful totals.
 */
function statsAreEmpty(o) {
    return statOf(o, ['totalLeads', 'leadsCount', 'total']) <= 0
        && statOf(o, ['pipelineValue', 'totalValue', 'value']) <= 0;
}

/**
 * Enrich a redacted GET response for demo mode. Identity no-op when demo is off,
 * the method isn't GET, the endpoint isn't allow-listed, or the screen already
 * has enough real data. Never throws.
 * @param {*} json the redacted response payload.
 * @param {string} url the request URL.
 * @param {string} [method='GET'] the HTTP method.
 * @returns {*} the enriched payload, or `json` unchanged.
 */
export function enrichResponse(json, url, method = 'GET') {
    if (!isDemoMode()) {
        return json;
    }
    if (String(method || 'GET').toUpperCase() !== 'GET') {
        return json;
    }
    try {
        const {path, query} = parseUrl(url);
        const entry = REGISTRY.find((e) => e.re.test(path));
        if (!entry) {
            return json;
        }

        // Never fabricate beyond the first page — infinite scroll must terminate.
        const page = Number(query.page);
        if (Number.isFinite(page) && page > 1) {
            return json;
        }

        if (entry.kind === 'stats') {
            return enrichStats(json, entry);
        }

        if (!hasBlueprint(entry.resource)) {
            return json;
        }

        const loc = locateList(json, entry.keys);
        if (!loc) {
            return json;
        }
        const target = TARGET_BY_RESOURCE[entry.resource] || DEFAULT_TARGET;
        const have = loc.arr.length;
        if (have >= target) {
            return json;
        }

        const ctx = buildContext(path, query, entry.resource);
        const fresh = buildEntities(entry.resource, target - have, ctx);
        if (!fresh.length) {
            return json;
        }
        const merged = loc.arr.concat(fresh);

        if (loc.bare) {
            return merged;
        }
        loc.container[loc.key] = merged;
        // Keep any total/count field consistent with the new length.
        TOTAL_KEYS.forEach((tk) => {
            if (typeof loc.container[tk] === 'number' && loc.container[tk] < merged.length) {
                loc.container[tk] = merged.length;
            }
        });

        return json;
    } catch (_) {
        return json; // fail-open — demo mode must never break a request
    }
}

/**
 * Synthesise a stats object in place of an empty one, preserving the envelope
 * the consumers read (`data.statistics || data.data || data`).
 * @param {*} json the redacted response.
 * @param {object} entry the registry entry.
 * @returns {*} the enriched response.
 */
function enrichStats(json, entry) {
    if (entry.resource !== 'crmStats') {
        return json;
    }
    const obj = json && typeof json === 'object' ? json : {};
    // Find where the consumer would read the stats from.
    const holder = (obj.statistics && typeof obj.statistics === 'object') ? obj.statistics
        : (obj.data && typeof obj.data === 'object' ? obj.data : obj);
    if (!statsAreEmpty(holder)) {
        return json;
    }
    const filled = synthCrmStats(holder);

    // Write it back at every shape the screens accept.
    if (obj.statistics && typeof obj.statistics === 'object') {
        obj.statistics = filled;
    } else if (obj.data && typeof obj.data === 'object') {
        obj.data = filled;
        obj.statistics = filled;
    } else {
        return {...obj, ...filled, statistics: filled};
    }

    return obj;
}
